import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { get } from 'lodash';
import {
  catchError,
  concatWith,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap
} from 'rxjs';
import { AuthActions } from '@components/auth/auth.actions';
import { ReportSkinHealthGuidelineActions } from './health-guideline.actions';
import { ReportSkinHealthGuidelineState } from './health-guideline.interface';
import { ReportSkinHealthGuidelineService } from './health-guideline.service';

/**
 * Report Skin Health Guideline Effects.
 *
 * @exports
 * @class ReportSkinHealthGuidelineEffects
 * @augments {ReportSkinHealthGuidelineService}
 */
@Injectable()
export class ReportSkinHealthGuidelineEffects extends ReportSkinHealthGuidelineService {
  private readonly actions = inject(Actions);

  readonly onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => ReportSkinHealthGuidelineActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly onFetch = createEffect(
    () => {
      return this.actions.pipe(
        ofType(ReportSkinHealthGuidelineActions.fetch),
        switchMap(() =>
          of(
            ReportSkinHealthGuidelineActions.setLoading({ loading: true })
          ).pipe(
            concatWith(
              // Fetch face blueprint, skin insight, and allergy sensitivity in parallel
              forkJoin({
                faceBlueprint: this.fetchSkinGuidelineFaceBlueprint(),
                skinInsight: this.fetchSkinGuidelineSkinInsight(),
                allergySensitivity: this.fetchSkinGuidelineAllergySensitivity()
              }).pipe(
                mergeMap(
                  ({ faceBlueprint, skinInsight, allergySensitivity }) => {
                    // Collect all trait IDs from both skinInsight and allergySensitivity
                    const skinInsightResults = get(skinInsight, 'results', []);
                    const allergySensitivityResults = get(
                      allergySensitivity,
                      'results',
                      []
                    );
                    const traitIds = [
                      ...skinInsightResults.map((r) => r.dnaTraitId),
                      ...allergySensitivityResults.map((r) => r.dnaTraitId)
                    ].filter(Boolean);

                    // Fetch all traits in parallel
                    return this.fetchTrait(traitIds as Array<number>).pipe(
                      map((traits) => {
                        // Map traits back to results by dnaTraitId
                        const traitMap = new Map<number, any>();
                        (traits.results ?? []).forEach((trait) => {
                          if (trait.dnaTrait?.id) {
                            traitMap.set(trait.dnaTrait.id, trait);
                          }
                        });

                        // Enhance skinInsight results
                        const enhancedSkinInsightResults =
                          skinInsightResults.map((result) => ({
                            ...result,
                            report: result.dnaTraitId
                              ? traitMap.get(result.dnaTraitId)
                              : undefined
                          }));

                        // Enhance allergySensitivity results
                        const enhancedAllergySensitivityResults =
                          allergySensitivityResults.map((result) => ({
                            ...result,
                            report: result.dnaTraitId
                              ? traitMap.get(result.dnaTraitId)
                              : undefined
                          }));

                        const updatedSkinInsight = {
                          ...skinInsight,
                          results: enhancedSkinInsightResults
                        };

                        const updatedAllergySensitivity = {
                          ...allergySensitivity,
                          results: enhancedAllergySensitivityResults
                        };

                        return [
                          ReportSkinHealthGuidelineActions.setFaceBlueprint({
                            faceBlueprint
                          }),
                          ReportSkinHealthGuidelineActions.setSkinInsight({
                            skinInsight:
                              updatedSkinInsight as ReportSkinHealthGuidelineState['skinInsight']
                          }),
                          ReportSkinHealthGuidelineActions.setAllergySensitivity(
                            {
                              allergySensitivity:
                                updatedAllergySensitivity as ReportSkinHealthGuidelineState['allergySensitivity']
                            }
                          ),
                          ReportSkinHealthGuidelineActions.setLoading({
                            loading: false
                          })
                        ];
                      }),
                      catchError(() =>
                        of([
                          ReportSkinHealthGuidelineActions.setLoading({
                            loading: false
                          })
                        ])
                      )
                    );
                  }
                ),
                // Flatten the array of actions
                mergeMap((actions) => actions)
              )
            )
          )
        )
      );
    },
    { dispatch: true }
  );
}
